import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon, MinusIcon, PlusIcon, ScanIcon, XIcon } from 'lucide-react';
import { useZoomPan, type Size } from './useZoomPan';
import { preloadImages } from '../../utils/preload';
export interface LightboxItem {
  src: string;
  alt: string;
  caption: string;
  /** Expected pixel size; replaced by the real size once the image loads. */
  width?: number;
  height?: number;
  file?: string;
}
interface LightboxProps {
  items: LightboxItem[];
  /** Index of the open item, or null when closed. */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  label: string;
  counterNoun?: string;
}

/** Window event other UI (the sticky purchase bar) listens to. */
export const LIGHTBOX_EVENT = 'pp-lightbox-toggle';
const iconButton = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-white/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/70 sm:h-12 sm:w-12';

/**
 * Full-screen viewer. Layout: top bar (caption + Close) · image area · bottom
 * bar (zoom controls). Only the image inside the image area is transformed, so
 * the controls can never be moved or covered. The image opens fitted to the
 * image area; zoom goes from 1× (fit) to 3×.
 */
export function Lightbox(props: LightboxProps) {
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence data-id="element-90" data-mp="383c9bp8rhzxp">{props.index !== null && <LightboxDialog key="lightbox" {...props} index={props.index} data-id="element-91" data-mp="33a0x18l6kqye" />}</AnimatePresence>, document.body);
}
function LightboxDialog({
  items,
  index,
  onClose,
  onNavigate,
  label,
  counterNoun = 'Página'
}: LightboxProps & {
  index: number;
}) {
  const current = items[index];
  const count = items.length;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<Size>({
    width: 0,
    height: 0
  });
  // Real size and load state are keyed by image URL, so they never leak between images.
  const [measured, setMeasured] = useState<{
    src: string;
    size: Size;
  } | null>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const loaded = loadedSrc === current.src;
  const natural: Size | null = measured && measured.src === current.src ? measured.size : current.width && current.height ? {
    width: current.width,
    height: current.height
  } : null;
  useEffect(() => {
    const neighbours = [items[index - 1], items[index + 1]].filter(item => item && item.src).map(item => item.src);
    preloadImages(neighbours);
  }, [index, items]);

  // Measure the image area (handles rotation, resize and mobile browser bars).
  useLayoutEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setStage(prev => Math.round(prev.width) === Math.round(rect.width) && Math.round(prev.height) === Math.round(rect.height) ? prev : {
        width: rect.width,
        height: rect.height
      });
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  // "Fit to screen" size, leaving a margin inside the image area.
  const pad = stage.width >= 640 ? 24 : 12;
  const fitted: Size | null = natural && stage.width > 0 && stage.height > 0 ? (() => {
    const ratio = Math.min(Math.max(1, stage.width - pad * 2) / natural.width, Math.max(1, stage.height - pad * 2) / natural.height);
    return {
      width: Math.floor(natural.width * ratio),
      height: Math.floor(natural.height * ratio)
    };
  })() : null;
  const go = useCallback((direction: 1 | -1) => {
    const next = index + direction;
    if (next >= 0 && next < count) onNavigate(next);
  }, [index, count, onNavigate]);
  const zoom = useZoomPan({
    fitted,
    stage,
    onSwipe: go
  });
  const {
    zoomIn,
    zoomOut,
    reset
  } = zoom;

  // Changing image always starts again from "fit to screen".
  useEffect(() => {
    reset();
  }, [index, reset]);

  // Scroll lock, focus handling and cleanup — always undone on close.
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const body = document.body;
    const previousOverflow = body.style.overflow;
    const previousOverscroll = body.style.overscrollBehavior;
    const scrollY = window.scrollY;
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'contain';
    window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
      detail: true
    }));
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus({
      preventScroll: true
    }));
    return () => {
      window.cancelAnimationFrame(frame);
      body.style.overflow = previousOverflow;
      body.style.overscrollBehavior = previousOverscroll;
      window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
        detail: false
      }));
      if (Math.abs(window.scrollY - scrollY) > 1) window.scrollTo({
        top: scrollY,
        behavior: 'auto'
      });
      previousFocus?.focus?.({
        preventScroll: true
      });
    };
  }, []);

  // Keyboard: Escape closes, arrows change page, +/- zoom, 0 fits.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        case 'ArrowRight':
          event.preventDefault();
          go(1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          go(-1);
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
          zoomOut();
          break;
        case '0':
          reset();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, go, zoomIn, zoomOut, reset]);

  // Focus trap inside the dialog.
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const nodes = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? []).filter(node => node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden');
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const onImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const src = current.src;
    if (img.naturalWidth && img.naturalHeight) {
      setMeasured({
        src,
        size: {
          width: img.naturalWidth,
          height: img.naturalHeight
        }
      });
    }
    setLoadedSrc(src);
  };
  return <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${label}: ${current.caption}`} onKeyDown={onKeyDown} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="fixed inset-0 z-50 flex w-full flex-col overflow-hidden bg-graphite-deep/[0.97] text-white" style={{
    height: '100dvh',
    maxHeight: '100dvh',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)'
  }} data-id="element-92" data-mp="dl8odf6nw1jd">
      {/* Top bar: caption + Close (never transformed) */}
      <div className="relative z-10 flex w-full min-w-0 shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-graphite-deep px-3 py-2 sm:px-6" data-id="element-93" data-mp="1pyp9fqbe6wh7">
        <div className="min-w-0" aria-live="polite" data-id="element-94" data-mp="1b9svjcmnmp8o">
          <p className="text-[0.8rem] font-bold text-coral" data-id="element-95" data-mp="1g8dsn36m0ssw">
            {counterNoun} {index + 1} de {count}
          </p>
          <p className="truncate text-[0.98rem] font-semibold leading-tight" data-id="element-96" data-mp="23xyh21lj8i9k">{current.caption}</p>
        </div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className={iconButton} data-id="element-97" data-mp="28qrjgitj3eih">
          <XIcon className="h-5 w-5" aria-hidden data-id="element-98" data-mp="3tdr8did80ji7" />
        </button>
      </div>

      {/* Image area: the only zone that scales and pans */}
      <div ref={stageRef} {...zoom.handlers} className="relative min-h-0 w-full flex-1 touch-none select-none overflow-hidden" style={{
      cursor: zoom.scale > 1 ? zoom.interacting ? 'grabbing' : 'grab' : 'zoom-in'
    }} data-id="element-99" data-mp="63b2zv1mdtks">
        {current.src ? <img key={current.src} src={current.src} alt={current.alt} draggable={false} onLoad={onImageLoad} className="absolute left-1/2 top-1/2 block rounded-[2px] bg-paper" style={{
        width: fitted ? `${fitted.width}px` : 'auto',
        height: fitted ? `${fitted.height}px` : 'auto',
        maxWidth: 'none',
        maxHeight: 'none',
        opacity: loaded && fitted ? 1 : 0,
        transformOrigin: 'center center',
        transform: `translate(-50%, -50%) translate3d(${zoom.offset.x}px, ${zoom.offset.y}px, 0) scale(${zoom.scale})`,
        transition: zoom.interacting ? 'opacity 150ms ease-out' : 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1), opacity 150ms ease-out'
      }} data-id="element-100" data-mp="35hwp42wk2od1" /> : <p className="absolute inset-0 flex items-center justify-center p-6 text-center text-white/70" data-id="element-101" data-mp="3rahkqeiv846y">
            Imagen no disponible
          </p>}

        {current.src && !loaded && <div className="pointer-events-none absolute inset-0 flex items-center justify-center" role="status" data-id="element-102" data-mp="3hcbfpo1rihxv">
            <span className="flex items-center gap-2 rounded-full bg-graphite/90 px-3.5 py-2 text-[0.85rem] font-semibold text-ivory ring-1 ring-white/15" data-id="element-103" data-mp="3db82l9b3n00c">
              <Loader2Icon className="h-4 w-4 animate-spin text-coral" aria-hidden data-id="element-104" data-mp="1fiudx10bcbb1" />
              Cargando página…
            </span>
          </div>}
      </div>

      {/* Bottom bar: page + zoom controls (never transformed) */}
      <div className="relative z-10 w-full min-w-0 shrink-0 border-t border-white/10 bg-graphite-deep px-2 pb-2 pt-2 sm:px-6" data-id="element-105" data-mp="2yft3b7hc44p2">
        <div className="mx-auto flex max-w-xl items-center justify-between gap-1.5" data-id="element-106" data-mp="337ufktei5vf3">
          <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Página anterior" className={iconButton} data-id="element-107" data-mp="378mpzpz426cy">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-108" data-mp="2ajou24fmk2e7" />
          </button>

          <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-109" data-mp="2fi02qbu6o6uu">
            <button type="button" onClick={zoomOut} disabled={!zoom.canZoomOut} aria-label="Alejar" className={iconButton} data-id="element-110" data-mp="2jj22cts0ikxc">
              <MinusIcon className="h-5 w-5" aria-hidden data-id="element-111" data-mp="2oiy8ozo6mog2" />
            </button>
            <span className="hidden w-12 text-center text-[14px] font-semibold tabular-nums text-white/80 min-[400px]:inline-block" data-id="element-112" data-mp="3uyh0i0vyjiu1">
              {Math.round(zoom.scale * 100)}%
            </span>
            <button type="button" onClick={zoomIn} disabled={!zoom.canZoomIn} aria-label="Acercar" className={iconButton} data-id="element-113" data-mp="1zu9k3s42qy0t">
              <PlusIcon className="h-5 w-5" aria-hidden data-id="element-114" data-mp="1vs5w6407y0yn" />
            </button>
            <button type="button" onClick={reset} aria-label="Ajustar a pantalla" title="Ajustar a pantalla" className={iconButton} data-id="element-115" data-mp="29ti906k31mgb">
              <ScanIcon className="h-5 w-5" aria-hidden data-id="element-116" data-mp="24v6x7qa891vg" />
            </button>
          </div>

          <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Página siguiente" className={iconButton} data-id="element-117" data-mp="1h52z1yft5ubu">
            <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-118" data-mp="1qvp6349glcix" />
          </button>
        </div>
        <p className="mt-1.5 hidden text-center text-[0.8rem] text-white/60 sm:block" data-id="element-119" data-mp="1m1td6g81yfum">
          Usá + y − o la rueda para acercar · arrastrá para moverte cuando esté ampliada
        </p>
      </div>
    </motion.div>;
}